import { Injectable } from '@angular/core';
import { BaseService } from '../../core/base.service';
import { ProductPayload } from './product-payload';
import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpProgressEvent,
} from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, timeout } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ProductPayloadErrorDialogComponent } from './product-payload-error-dialog/product-payload-error-dialog.component';
import { ProductPayloadProgressComponent } from './product-payload-progress/product-payload-progress.component';

export interface PayloadFileNames {
  files: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ProductPayloadService extends BaseService<ProductPayload> {
  uploadProgress$: BehaviorSubject<number> = new BehaviorSubject(0);
  currentProductID = '';

  constructor(
    protected override readonly httpClient: HttpClient,
    public dialog: MatDialog
  ) {
    super(httpClient);
    this.apiUrl += 'product_payloads';
  }

  getUploadProgress(): Observable<number> {
    return this.uploadProgress$.asObservable();
  }

  errorDialog() {
    this.dialog.closeAll();
    this.dialog.open(ProductPayloadErrorDialogComponent, {
      disableClose: false,
    });
  }

  progressDialog() {
    this.uploadProgress$.next(0);
    this.dialog.open(ProductPayloadProgressComponent, {
      disableClose: false,
    });
  }

  createProductPayload(
    productPayload: ProductPayload
  ): Observable<HttpEvent<HttpProgressEvent>> {
    const formData = new FormData();
    formData.append('product', productPayload.product);
    productPayload.files.forEach((file) => formData.append('files', file));
    this.progressDialog();
    return this.httpClient
      .post<HttpProgressEvent>(this.apiUrl, formData, {
        reportProgress: true,
        observe: 'events',
        headers: new HttpHeaders({ 'ngsw-bypass': 'true' }),
      })
      .pipe(
        timeout(60000),
        catchError((err) => {
          console.log(err);
          this.errorDialog();
          return EMPTY;
        }),
        this.uploadProgress((progress) => this.uploadProgress$.next(progress))
      );
  }

  updateProductPayload(
    productPayload: ProductPayload
  ): Observable<HttpEvent<HttpProgressEvent>> {
    const formData = new FormData();
    formData.append('product', productPayload.product);
    productPayload.files.forEach((file) => formData.append('files', file));
    this.progressDialog();
    return this.httpClient
      .put<HttpProgressEvent>(this.apiUrl, formData, {
        reportProgress: true,
        observe: 'events',
        headers: new HttpHeaders({ 'ngsw-bypass': 'true' }),
      })
      .pipe(
        timeout(60000),
        catchError((err) => {
          console.log(err);
          this.errorDialog();
          return EMPTY;
        }),
        this.uploadProgress((progress) => this.uploadProgress$.next(progress))
      );
  }

  downloadProduct(productID: string): Observable<Blob> {
    return this.httpClient.get(`${this.apiUrl}/${productID}/download`, {
      headers: new HttpHeaders({ 'ngsw-bypass': 'true' }),
      responseType: 'blob',
    });
  }

  listPayloadFiles(productID: string): Observable<string[]> {
    return this.httpClient
      .get<PayloadFileNames>(`${this.apiUrl}/${productID}/files`, {
        headers: new HttpHeaders({ 'ngsw-bypass': 'true' }),
      })
      .pipe(map((response) => response.files));
  }

  uploadProgress<T>(cb: (progress: number) => void) {
    return tap((event: HttpEvent<T>) => {
      if (event.type === HttpEventType.UploadProgress) {
        cb(Math.round((100 * event.loaded) / event.total!));
      }
    });
  }

  deletePayloadFile(fileName: string): Observable<unknown> {
    return this.httpClient.delete(
      `${this.apiUrl}/${this.currentProductID}/${fileName}`,
      this.HTTP_OPTIONS
    );
  }
}
